import { Component, DestroyRef, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ImageModule } from 'primeng/image';

import { FotoMedicoCacheService } from './foto-medico-cache.service';

/**
 * Retrato do médico ao lado dos dados dele.
 *
 * <p>Cascata de três degraus: foto do cadastro → iniciais do nome → nada. O último é o caso do cargo
 * vago: sem nome não há quem retratar, e um bloco vazio ali seria ruído num card que não tem
 * ninguém. Quem consome checa {@link temConteudo} para decidir se desenha a coluna.
 *
 * <p>A foto é resolvida pelo próprio componente, a partir de CRM e UF — o consumidor não precisa
 * saber de endpoint nem de cache.
 */
@Component({
    selector: 'app-foto-diretor',
    standalone: true,
    imports: [ImageModule],
    templateUrl: './foto-diretor.html',
    styleUrl: './foto-diretor.scss',
    host: {
        '[class.foto-diretor--vazio]': '!temConteudo()',
        '[attr.aria-hidden]': 'true'
    }
})
export class FotoDiretor {
    nome = input<string | null>(null);
    crm = input<number | string | null>(null);
    uf = input<string | null>(null);

    private readonly cache = inject(FotoMedicoCacheService);
    private readonly destroyRef = inject(DestroyRef);

    protected readonly foto = signal<string | null>(null);

    /** Iniciais do nome, no máximo duas — o mesmo recorte que os avatares do resto do sistema usam. */
    protected readonly iniciais = computed(() => {
        const limpo = (this.nome() ?? '').trim();
        if (!limpo || limpo === '-') {
            return '';
        }
        return limpo
            .split(/\s+/)
            .slice(0, 2)
            .map((parte) => parte[0])
            .join('')
            .toUpperCase();
    });

    /** Sem nome não há retratado: o host se apaga e o formulário fica com a largura toda. */
    readonly temConteudo = computed(() => !!this.iniciais());

    constructor() {
        effect(() => {
            const crm = this.crm();
            const uf = this.uf();
            const temNome = this.iniciais();

            // Sem nome nem chave não há o que buscar; limpar evita o retrato do médico anterior
            // sobreviver a uma troca de prestador.
            if (!temNome || !crm || !uf) {
                untracked(() => this.foto.set(null));
                return;
            }

            untracked(() => {
                this.foto.set(this.cache.emCache(crm, uf));
                this.cache
                    .buscar(crm, uf)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe((foto) => this.foto.set(foto));
            });
        });
    }
}
